import { serviceUnavailableMessage } from "../../../../../constants/serviceUnavailableMessages";
import { ALL_KINDS, type ImportCounts, type ImportKind } from "../../utils/bpaImport";

export const KIND_LABEL: Record<ImportKind, string> = {
  submissions: "Submissions",
  rankings: "Rankings",
  commentary: "Commentary",
};

const KIND_NOUN: Record<ImportKind, [string, string]> = {
  submissions: ["submission", "submissions"],
  rankings: ["ranking", "rankings"],
  commentary: ["commentary entry", "commentary entries"],
};

function countPhrase(kind: ImportKind, count: number): string {
  const [one, many] = KIND_NOUN[kind];
  return `${count} ${count === 1 ? one : many}`;
}

function joinWithAnd(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/** "2 submissions, 2 rankings and 1 commentary entry" — only the chosen kinds. */
export function describeCounts(counts: ImportCounts, kinds: ImportKind[] = ALL_KINDS): string {
  const chosen = ALL_KINDS.filter(kind => kinds.includes(kind));
  return joinWithAnd(chosen.map(kind => countPhrase(kind, counts[kind])));
}

export function formatImportDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

/** "SARs overall 8/10" — the score is out of ten; a submission without one reads "no SARs score". */
export function sarsScoreText(score: number | null): string {
  return score === null ? "no SARs score" : `SARs overall ${score}/10`;
}

/** Why a replace or remove choice is disabled for a user who cannot delete: it removes target content. */
export const REPLACE_NEEDS_DELETE_REASON =
  "Using the source's content removes target content and needs delete permission.";

/** Why Move is disabled for a user who cannot delete: it removes the content from the source. */
export const MOVE_NEEDS_DELETE_REASON =
  "Moving deletes the content from the source and needs delete permission.";

/** What the archive confirmation says. One wording shared by every place that archives a BPA. */
function endsMergeText(mergeSourceCount: number): string {
  if (mergeSourceCount === 0) return "";
  const sources =
    mergeSourceCount === 1
      ? "the practice area scheduled to merge into it goes back to normal"
      : `the ${mergeSourceCount} practice areas scheduled to merge into it go back to normal`;
  return ` It also ends the merge: ${sources}.`;
}

export function archiveConfirmMessage(name: string, mergeSourceCount = 0): string {
  return `Archive "${name}"? This will hide it from the default edition list.${endsMergeText(mergeSourceCount)}`;
}

function unreachableService(error: Error): string | null {
  if ((error as { status?: number }).status !== 503) return null;
  return /^Failed to connect to (\S+)$/.exec(error.message)?.[1] ?? null;
}

export function messageOf(error: unknown): string {
  if (!(error instanceof Error)) return "Something went wrong. Try again.";
  const service = unreachableService(error);
  return service ? serviceUnavailableMessage(service) : error.message;
}
